import React, { useEffect, useState, useMemo } from "react";
import { Box, TextField } from "@material-ui/core";
import { useMobileStyles } from "./styles.js";
import { PAGES, STATUS } from "../../../utils/constant";
import { Header } from "../../../components";
import ToastMessage from "../../../components/ToastMessage";
import HCServices from "../../../services/hcServices.js";
import Icon from "../../../utils/icons.js";
import { getGlobalState, setGlobalState } from "../../../utils/globalState";
import { isStateChanged, updateAirSytemInLocalStorage, getPermissionDeniedToastMessage, validateName, isWeb } from "../../../utils/utility";
import I18n from "../../../localization";
import _ from "lodash";

const initialDetails = {
  hvacName: "",
  energySource: "",
  typeOfSystem: "",
  heaterRating: "",
  coolerRating: "",
}

const HvacDetails = (props) => {
  const classes = useMobileStyles();

  const [hvacDetails, setHvacDetails] = useState(initialDetails);
  const [tempHvacDetails, setTempHvacDetails] = useState(initialDetails);

  const [errors, setErrors] = useState({});
  const [errorMessages, setErrorMessages] = useState({});
  const [loader, setLoader] = useState(true);
  const [savingLoader, setSavingLoader] = useState(false);
  const [disableBtn, setDisableBtn] = useState(false)

  const airDeviceName=getGlobalState("airDeviceName")

  const [toast, setToast] = useState({
    status: false,
    message: null,
    type: "success",
    isPermissionDeniedMsg:false
  });

  const SystemTypeMap = {
    HEAT: 'Heat',
    COOL: 'Cool',
    HEAT_COOL: 'Heat and Cool',
    UNKNOWN: 'Unknown',
  }

  // ------------------------------------------------------------
  // Validation Logic
  // ------------------------------------------------------------
  const validateField = (key, value) => {
    let message = "";

    if (!value.trim()) {
      if (!["heaterRating", "coolerRating"].includes(key)) {
        message = I18n.t("mobile.hvac.message.errorMsgLine1")
      }
    } else if (key === "hvacName") {
      const status = validateName(value)
      if(status !== true) {
        message = status
      }
    } else if (["heaterRating", "coolerRating"].includes(key) && isNaN(value)) {
      message = I18n.t("mobile.hvac.message.errorMsgLine3")
    } else if (["heaterRating", "coolerRating"].includes(key) && (value < 0 || value > 10000)) {
      message = I18n.t("mobile.hvac.message.errorMsgLine4")
    }

    setErrors((prev) => ({ ...prev, [key]: !!message }));
    setErrorMessages((prev) => ({ ...prev, [key]: message }));

    return !message;
  };

    const validateAll = () => {
      const fieldsToValidate = ["hvacName"];

      return fieldsToValidate.every((key) => {
        const isValid = validateField(key, hvacDetails[key]);
        return isValid;
      });
    };


  // ------------------------------------------------------------
  // Handlers
  // ------------------------------------------------------------
  const handleChange = (key, value) => {
    setHvacDetails((prev) => ({ ...prev, [key]: value }))
    const isValid = validateField(key, value)
    setDisableBtn(!isValid)
  }

  const handleSave = async () => {
    if(!props.hasAccess){
      setToast({status:true,message:getPermissionDeniedToastMessage(props.maskedOwnerEmail),type:'error',isPermissionDeniedMsg:true})
      return 
    }
    if (!validateAll()) return;

    try {
      setSavingLoader(true);
      const payload = {
        name: hvacDetails.hvacName,
        "heat-rating": hvacDetails.heaterRating,
        "cool-rating": hvacDetails.coolerRating,
      };

      const res = await HCServices.setHVACDetails(
        props.siteId,
        props.airDeviceId,
        payload
      );

      if (res.message !== STATUS.SUCCESS) throw new Error();

      // Update temp copy
      setTempHvacDetails(hvacDetails);

      // Update global name if changed
      setGlobalState("airDeviceName", hvacDetails.hvacName);
      updateAirSytemInLocalStorage(props.airDeviceId, payload)

      // Success toast
      setToast({
        status: true,
        message: I18n.t("mobile.hvac.message.success"),
        type: "success",
      });
    } catch (err) {
      setToast({
        status: true,
        message: I18n.t("mobile.hvac.message.error"),
        type: "error",
      });
    } finally {
      setSavingLoader(false);
    }
  };

  const handleBack = () => {
    props.handleClick(PAGES.AIR_SYSTEM_SETTING);
  };

  // ------------------------------------------------------------
  // Fetch Details
  // ------------------------------------------------------------
  useEffect(() => {
    fetchDetails();
  }, [props.airDeviceName, props.siteId]);

  useEffect(()=>{
    if(airDeviceName && isWeb() && props.updateHeaderText){
      function getTitle() {
      
        return (
          <div className={classes.iqAirTitleContainer}>
            <span>{I18n.t("mobile.hvac.hvacDetails")}</span>
            {airDeviceName && <span>{airDeviceName}</span>}
          </div>
        );
      }
      props.updateHeaderText(getTitle())
    }
  
  },[airDeviceName])


  const fetchDetails = async () => {
    setLoader(true);
    try {
      const res = await HCServices.getHVACDetails(
        props.siteId,
        props.airDeviceId
      );

      if (!res || res.status !== "success") return;

      const device = res.data.find((d) => d["device-id"] === props.airDeviceId);
      if (!device) return;

      const hvacObj = {
        hvacName: device.name || "",
        energySource: device["energy-source"] || "",
        typeOfSystem: SystemTypeMap[device.type] || "",
        heaterRating: device["heat-rating"]?.toString() || "",
        coolerRating: device["cool-rating"]?.toString() || "",
      };

      setHvacDetails(hvacObj);
      setTempHvacDetails(hvacObj);
    } catch (err) {
      console.error("Error fetching HVAC details:", err);
    } finally {
      setLoader(false);
    }
  };

  // ------------------------------------------------------------
  // Detect Page Change
  // ------------------------------------------------------------
  const isPageDataChanged = useMemo(() => {
    return !loader && !_.isEqual(hvacDetails, tempHvacDetails);
  }, [hvacDetails, tempHvacDetails, loader]);

  // ------------------------------------------------------------
  // UI
  // ------------------------------------------------------------

  return (
    <>
      {!props.isWeb && (
        <Header
          title={I18n.t("mobile.hvac.hvacDetails")}
          onClick={handleBack}
          label={airDeviceName}
        />
      )}

      {loader ? <Box className={classes.loaderContainer}>
                  <Icon src="loaderV4" width="60px" height="60px" />
               </Box>
      :
      <Box className={classes.modelcontainer}>
        <Box className={classes.modelBody}>
          <Box className={classes.DetailsContainer}>
            {/* HVAC Name */}
            <Box className={classes.TextFieldSection}>
              <TextField
                required
                label={I18n.t("mobile.hvac.hvacName")}
                className={classes.inputText}
                value={hvacDetails.hvacName}
                onChange={(e) => handleChange("hvacName", e.target.value)}
                error={errors.hvacName}
                helperText={
                  errors.hvacName && (
                    <span className={classes.fieldEmtyErrorMessage}>
                      {errorMessages.hvacName}
                    </span>
                  )
                }
              />
            </Box>

            {/* Type of System */}
            {hvacDetails.typeOfSystem && <Box className={classes.TextFieldSection}>
              <TextField
                label={I18n.t("mobile.hvac.typeOfSystem")}
                className={classes.inputText}
                value={hvacDetails.typeOfSystem}
                InputProps={{ readOnly: true, disabled: true }}
              />
            </Box>}

            
            {hvacDetails.energySource && <Box className={classes.TextFieldSection}>
              <TextField
                label={I18n.t("mobile.hvac.energySource")}
                className={classes.inputText}
                value={hvacDetails.energySource.toLowerCase()}
                InputProps={{ readOnly: true, disabled: true }}
              />
            </Box>}

            {/* Heater Rating */}
            <Box className={classes.TextFieldSection}>
              <TextField
                label={I18n.t("mobile.hvac.heaterRating")}
                className={classes.inputText}
                value={hvacDetails.heaterRating}
                onChange={(e) => handleChange("heaterRating", e.target.value)}
                error={errors.heaterRating}
                helperText={
                  errors.heaterRating && (
                    <span className={classes.fieldEmtyErrorMessage}>
                      {errorMessages.heaterRating}
                    </span>
                  )
                }
              />
            </Box>

            {/* Cooler Rating */}
            <Box className={classes.TextFieldSection}>
              <TextField
                label={I18n.t("mobile.hvac.coolerRating")}
                className={classes.inputText}
                value={hvacDetails.coolerRating}
                onChange={(e) => handleChange("coolerRating", e.target.value)}
                error={errors.coolerRating}
                helperText={
                  errors.coolerRating && (
                    <span className={classes.fieldEmtyErrorMessage}>
                      {errorMessages.coolerRating}
                    </span>
                  )
                }
              />
            </Box>
          </Box>
        </Box>

        {/* Save Button */}
        <Box className={classes.buttonContainer}>
          <Box
            className={`${isPageDataChanged && !disableBtn ? classes.enableButton : ""} ${
              classes.buttonStyle
            }`}
            onClick={handleSave}
          >
            {savingLoader ? (
              <Icon
                radius="35"
                stroke="#fff"
                width="15px"
                height="15px"
                src="loaderV4"
                strokeWidth="8px"
              />
            ) : (
              I18n.t("mobile.hvac.save")
            )}
          </Box>
        </Box>
      </Box>}

      {/* Toast Message */}
      {toast.status && (
        <ToastMessage
          shouldDisplay={toast.status}
          content={toast.message}
          handleClose={() => setToast({ status: false, message: null })}
          type={toast.type}
          isWeb={false}
          isPermissionDeniedMsg={toast.isPermissionDeniedMsg}
        />
      )}
    </>
  );
};

export default HvacDetails;
